/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const permissions = currentUser?.permissions || [];

  // 根据权限codes动态生成权限控制
  // 将权限codes转换为Set，方便快速查找
  const permissionSet = new Set(permissions);

  // 检查是否拥有某个权限
  const hasPermission = (code: string) => permissionSet.has(code);
  const hasAnyErrandPermission = permissions.some((code) =>
    code.startsWith('errand:'),
  );

  const canViewErrandPricing = hasPermission('errand:pricing:view');
  const canCreateErrandPricing = hasPermission('errand:pricing:create');
  const canUpdateErrandPricing = hasPermission('errand:pricing:update');

  return {
    // 菜单权限 - 根据权限code判断
    canHome: hasPermission('home'),
    canSystem: hasPermission('system'),
    canUserManage: hasPermission('system:user'),
    canRoleManage: hasPermission('system:role'),
    canPermissionManage:
      hasPermission('permission:view') ||
      hasPermission('permission:manage') ||
      hasPermission('system:permission'),
    canWxUsers: hasPermission('wxusers'),
    canWxUserManage:
      hasPermission('wxusers:user') || hasPermission('wxusers:user:list'),
    canWxUserView: hasPermission('wxusers:user:view'),

    // 产品模块菜单权限
    canProduct: hasPermission('product'),
    canProductCategory: hasPermission('product:category'),
    canProductManage: hasPermission('product:product'),

    // 系统管理按钮权限
    canUserCreate: hasPermission('system:user:create'),
    canUserEdit:
      hasPermission('system:user:edit') || hasPermission('system:user:update'),
    canUserDelete: hasPermission('system:user:delete'),
    canRoleCreate: hasPermission('system:role:create'),
    canRoleEdit:
      hasPermission('system:role:edit') || hasPermission('system:role:update'),
    canRoleDelete: hasPermission('system:role:delete'),
    canPermissionCreate: hasPermission('system:permission:create'),
    canPermissionEdit:
      hasPermission('system:permission:edit') ||
      hasPermission('system:permission:update'),
    canPermissionDelete: hasPermission('system:permission:delete'),

    // 产品分类按钮权限
    canCategoryList: hasPermission('product:category:list'),
    canCategoryCreate: hasPermission('product:category:create'),
    canCategoryUpdate: hasPermission('product:category:update'),
    canCategoryDelete: hasPermission('product:category:delete'),

    // 产品管理按钮权限
    canProductList: hasPermission('product:product:list'),
    canProductView: hasPermission('product:product:view'),
    canProductCreate: hasPermission('product:product:create'),
    canProductUpdate: hasPermission('product:product:update'),
    canProductDelete: hasPermission('product:product:delete'),
    canInventoryUpdate: hasPermission('product:inventory:update'),
    canMediaUpload: hasPermission('product:media:upload'),

    // 机器设备模块菜单权限
    canMachine: hasPermission('machine'),
    canMachineDevice:
      hasPermission('machine:device') || hasPermission('machine:device:list'),
    canMachineExchange:
      hasPermission('machine:exchange') ||
      hasPermission('machine:exchange:create'),

    // 机器设备按钮权限
    canMachineDeviceView: hasPermission('machine:device:view'),
    canMachineExchangeCreate: hasPermission('machine:exchange:create'),

    // 订单管理模块菜单权限
    canOrder: hasPermission('order'),
    canOrderList: hasPermission('order:list'),
    // 订单管理按钮权限
    canOrderView: hasPermission('order:view'),
    canOrderStats: hasPermission('order:stats'),
    canOrderExport: hasPermission('order:export'),
    canOrderRefund: hasPermission('order:refund'),

    // 工单管理模块菜单权限
    canTicket: hasPermission('ticket'),
    canTicketList: hasPermission('ticket:list'),
    canTicketStats: hasPermission('ticket:stats'),

    // 工单管理按钮权限
    canTicketView: hasPermission('ticket:view'),
    canTicketReply: hasPermission('ticket:reply'),
    canTicketClose: hasPermission('ticket:close'),

    // 跑腿管理模块菜单权限
    canErrand: hasAnyErrandPermission,
    canListErrandSchool: hasPermission('errand:school:list'),
    canViewErrandSchool: hasPermission('errand:school:view'),
    canCreateErrandSchool: hasPermission('errand:school:create'),
    canUpdateErrandSchool: hasPermission('errand:school:update'),
    canListErrandRider: hasPermission('errand:rider:list'),
    canViewErrandRider: hasPermission('errand:rider:view'),
    canReviewErrandRider: hasPermission('errand:rider:review'),
    canManageErrandRider: hasPermission('errand:rider:manage'),
    canListErrandPartner: hasPermission('errand:partner:list'),
    canViewErrandPartner: hasPermission('errand:partner:view'),
    canReviewErrandPartner: hasPermission('errand:partner:review'),
    canListErrandWithdrawal: hasPermission('errand:withdrawal:list'),
    canReviewErrandWithdrawal: hasPermission('errand:withdrawal:review'),
    canListErrandPricing: hasPermission('errand:pricing:list'),
    canViewErrandPricing,
    canCreateErrandPricing,
    canUpdateErrandPricing,
    canAccessErrandPricingEditor:
      canViewErrandPricing || canCreateErrandPricing || canUpdateErrandPricing,
    canListErrandOrder: hasPermission('errand:order:list'),
    canViewErrandOrder: hasPermission('errand:order:view'),
    canStatsErrandOrder: hasPermission('errand:order:stats'),
    canSettleErrandOrder: hasPermission('errand:order:settle'),
    canCancelErrandOrder: hasPermission('errand:order:cancel'),
    canExportErrandOrder: hasPermission('errand:order:export'),
  };
}
